<script setup lang="ts">
import dayjs from 'dayjs'

import CardTitle from '@/components/CardTitle.vue'

import type { CustomerDetails, MerchantAttributes } from '@/types/types'

const props = defineProps<{
  customer: CustomerDetails
  customerAttributes?: Record<string, string>
  merchantAttributes?: MerchantAttributes[]
}>()
const { t } = useI18n()

const birthDate = computed(() => (props.customer.birthDate ? dayjs(props.customer.birthDate).format('L') : '-'))
</script>
<template>
  <div class="w-full rounded-lg border bg-white">
    <div class="ml-4 flex items-center py-5 sm:ml-6">
      <CardTitle
        :title="t('pages.customer.customerDetailPage.blocks.identification.title')"
        :subtitle="
          t(
            `pages.customer.customerDetailPage.blocks.identification.description.${['man', 'woman'].includes(customer.gender) ? customer.gender : 'unknown'}`
          )
        "
        name="HeroUserIconOutline"
      />
    </div>
    <div class="border-t px-4 py-5 sm:px-6">
      <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
        <div class="sm:col-span-1">
          <dt class="text-sm font-medium text-muted-foreground">
            {{ t('pages.customer.customerDetailPage.blocks.identification.phone') }}
          </dt>
          <dd class="mt-1 text-sm text-foreground">
            {{ customer.phone ?? '-' }}
          </dd>
        </div>
        <div class="sm:col-span-1">
          <dt class="text-sm font-medium text-muted-foreground">
            {{ t('pages.customer.customerDetailPage.blocks.identification.mail') }}
          </dt>
          <dd class="mt-1 text-sm text-foreground">
            {{ customer.email ?? '-' }}
          </dd>
        </div>
        <div v-if="customer.loyaltyCardNumber" class="sm:col-span-1">
          <dt class="text-sm font-medium text-muted-foreground">
            {{ t('pages.customer.customerDetailPage.blocks.identification.loyaltyCardNumber') }}
          </dt>
          <dd class="mt-1 text-sm text-foreground">
            {{ customer.loyaltyCardNumber ?? '-' }}
          </dd>
        </div>
        <div class="sm:col-span-1">
          <dt class="text-sm font-medium text-muted-foreground">
            {{ t('pages.customer.customerDetailPage.blocks.identification.dob') }}
          </dt>
          <dd class="mt-1 text-sm text-foreground">
            {{ birthDate }}
          </dd>
        </div>
        <div class="sm:col-span-1">
          <dt class="text-sm font-medium text-muted-foreground">
            {{ t('pages.customer.customerDetailPage.blocks.identification.postalCode') }}
          </dt>
          <dd class="mt-1 text-sm text-foreground">
            {{ customer.postalCode ?? '-' }}
          </dd>
        </div>
        <div class="sm:col-span-1">
          <dt class="text-sm font-medium text-muted-foreground">
            {{ t('global.gender.name') }}
          </dt>
          <dd class="mt-1 text-sm text-foreground">
            {{ customer.gender ? t(`global.gender.${customer.gender}`) : t('global.gender.unknown') }}
          </dd>
        </div>
        <div class="sm:col-span-1">
          <dt class="text-sm font-medium text-muted-foreground">
            {{ t('global.gender.clientId') }}
          </dt>
          <dd class="mt-1 text-sm text-foreground">
            {{ customer.publicId }}
          </dd>
        </div>
        <div class="sm:col-span-1">
          <dt class="text-sm font-medium text-muted-foreground">
            {{ t('pages.customer.customerDetailPage.blocks.identification.enrollmentSource') }}
          </dt>
          <dd class="mt-1 text-sm text-foreground">
            {{ customer.enrollmentSource ?? '-' }}
          </dd>
        </div>
        <template v-for="attribute in merchantAttributes" :key="attribute.id">
          <div class="sm:col-span-1">
            <dt class="text-sm font-medium text-muted-foreground">
              {{ attribute.label }}
            </dt>
            <dd class="mt-1 text-sm text-foreground">
              <span v-if="attribute.type === 'date'">
                {{
                  customerAttributes[attribute.id] ? dayjs(customerAttributes[attribute.id]).format('L') : '-'
                }} </span
              ><span v-else>
                {{ customerAttributes[attribute.id] ?? '-' }}
              </span>
            </dd>
          </div>
        </template>
        <div class="sm:col-span-1">
          <dt class="text-sm font-medium text-muted-foreground">
            {{ t('pages.customer.customerDetailPage.blocks.identification.wasLoggedInToWebapp') }}
          </dt>
          <dd class="mt-1 text-sm text-foreground">
            {{ customer.firstLogInToWebappAt ? t('global.yes') : t('global.no') }}
          </dd>
        </div>
      </dl>
    </div>
  </div>
</template>
